// @src/layout/Header.tsx
'use client'

import { useEffect, useMemo, useState, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import { Menu, UserCircleIcon, X } from 'lucide-react'
import { User } from '@supabase/supabase-js'

import { Button } from '@/components/ui/button'
import Logo from '@/components/common/Logo'
import { useSupabase } from '@/hooks/useSupabase'
import { useProfileMode } from '@/hooks/useProfileMode'

import Navigator from './header/Navigator'
import UserMenu from './header/UserMenu'
import NotificationMenu from './header/NotificationMenu'
import ClientNotificationMenu from './header/ClientNotificationMenu'
import FreelancerNotificationMenu from './header/FreelancerNotificationMenu'
import MobileMenu from './header/mobile/MobileMenu'
import { useNotifications } from './header/hooks/useNotifications'
import { getScopeByPathname, getPathContext } from './header/utils/getScopeByPathname'
import { getPublicClientMenu, getPublicFreelancerMenu, getDesktopHeaderMenu, type HeaderMenuGroup } from './header/utils/headerMenuConfig'


// ✅ 랜딩(로고 클릭 목적지)
const FREELANCER_LANDING = '/'
const CLIENT_LANDING = '/client'

// ✅ Header에서 필요한 최소한의 User 정보만 정의 (타입 안전성)
type HeaderUser = Pick<User, 'id' | 'email'> | null

interface HeaderProps {
  /**
   * 서버에서 확인한 초기 사용자 정보
   * 첫 렌더부터 정확한 메뉴를 표시하기 위해 서버에서 전달됨
   */
  initialUser?: { id: string; email?: string } | null
}

export default function Header({ initialUser: serverUser }: HeaderProps = {}) {
  const router = useRouter()
  const pathname = usePathname() || ''

  const { supabase, user: clientUser, loading: authLoading } = useSupabase()
  const { loading: modeLoading, hasCompany, hasFreelancer, isCompany, isFreelancer } = useProfileMode()
  
  // ✅ 서버에서 확인한 user를 우선 사용, 없으면 클라이언트 user 사용
  // 서버 user가 있으면 즉시 사용하여 메뉴 깜빡임 방지
  // 타입 안전성을 위해 Pick<User, 'id' | 'email'>만 사용
  const user: HeaderUser = serverUser
    ? { id: serverUser.id, email: serverUser.email ?? '' }
    : (clientUser ? { id: clientUser.id, email: clientUser.email ?? '' } : null)

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const [showNotificationMenu, setShowNotificationMenu] = useState(false)
  const [isNotificationMenuPinned, setIsNotificationMenuPinned] = useState(false)
  const [processingMessageId, setProcessingMessageId] = useState<string | number | null>(null)

  // ✅ 로그인 상태를 메모이제이션: 초기 로드 완료 후에는 변경되지 않도록
  // 서버에서 확인한 user를 초기값으로 사용 (첫 렌더부터 확정)
  const [memoizedUser, setMemoizedUser] = useState<HeaderUser>(serverUser ? { id: serverUser.id, email: serverUser.email ?? '' } : null)
  const [isAuthInitialized, setIsAuthInitialized] = useState(!!serverUser)
  
  // ✅ 프로필 모드 정보도 메모이제이션: 로딩 중에는 이전 값 유지
  const [memoizedHasCompany, setMemoizedHasCompany] = useState(false)
  const [memoizedHasFreelancer, setMemoizedHasFreelancer] = useState(false)
  const [isProfileModeInitialized, setIsProfileModeInitialized] = useState(false)

  // ✅ 인증 로딩이 완료된 후에만 user 상태를 메모이제이션 (초기 로드 시 한 번만)
  // 서버에서 확인한 user가 있으면 즉시 사용
  useEffect(() => {
    // 서버에서 user를 받았으면 즉시 메모이제이션
    if (serverUser && !isAuthInitialized) {
      setMemoizedUser(user)
      setIsAuthInitialized(true)
      return
    }
    
    // authLoading이 false가 되고, 아직 초기화되지 않았을 때만 실행
    if (!authLoading && !isAuthInitialized) {
      setMemoizedUser(user)
      setIsAuthInitialized(true)
    }
  }, [authLoading, user, isAuthInitialized, serverUser])

  // ✅ 로그인/로그아웃 이벤트 발생 시에만 업데이트 (메뉴 깜빡임 방지)
  useEffect(() => {
    if (!supabase) return

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // 로그인/로그아웃 이벤트 발생 시에만 상태 업데이트
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        const newUser: HeaderUser = session?.user
          ? { id: session.user.id, email: session.user.email ?? '' }
          : null
        const wasLoggedIn = memoizedUser !== null
        const isNowLoggedIn = newUser !== null
        
        // 로그인 상태가 실제로 변경된 경우에만 업데이트 (깜빡임 방지)
        if (wasLoggedIn !== isNowLoggedIn) {
          setMemoizedUser(newUser)
          // 로그아웃 시 프로필 모드 정보도 초기화
          if (event === 'SIGNED_OUT') {
            setMemoizedHasCompany(false)
            setMemoizedHasFreelancer(false)
            setIsProfileModeInitialized(false)
          }
        }
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [supabase, memoizedUser])

  // ✅ 프로필 모드 정보 메모이제이션: 로딩 완료 후에만 업데이트
  useEffect(() => {
    if (!modeLoading && !isProfileModeInitialized) {
      // 초기 로딩 완료 시 한 번만 설정
      setMemoizedHasCompany(hasCompany)
      setMemoizedHasFreelancer(hasFreelancer)
      setIsProfileModeInitialized(true)
    } else if (!modeLoading && isProfileModeInitialized) {
      // 이미 초기화된 후에는 값이 실제로 변경되었을 때만 업데이트 (깜빡임 방지)
      if (memoizedHasCompany !== hasCompany) {
        setMemoizedHasCompany(hasCompany)
      }
      if (memoizedHasFreelancer !== hasFreelancer) {
        setMemoizedHasFreelancer(hasFreelancer)
      }
    }
  }, [modeLoading, hasCompany, hasFreelancer, isProfileModeInitialized, memoizedHasCompany, memoizedHasFreelancer])

  // ✅ 실제 사용할 user: 
  // - 초기 로드 중(authLoading && !isAuthInitialized): null (메뉴 깜빡임 방지)
  // - 로딩 완료 후: 메모이제이션된 값 사용
  // - 로딩 중이지만 이미 초기화됨: 메모이제이션된 값 유지 (절대 변경되지 않음)
  const effectiveUser = useMemo(() => {
    return authLoading && !isAuthInitialized ? null : memoizedUser
  }, [authLoading, isAuthInitialized, memoizedUser])
  
  // ✅ 실제 사용할 프로필 모드 정보: 
  // - 초기 로딩 중(!isProfileModeInitialized): false (깜빡임 방지)
  // - 로딩 완료 후: 메모이제이션된 값 사용
  // - 로딩 중이지만 이미 초기화됨: 메모이제이션된 값 유지
  const effectiveHasCompany = !isProfileModeInitialized ? false : memoizedHasCompany
  const effectiveHasFreelancer = !isProfileModeInitialized ? false : memoizedHasFreelancer

  // ✅ 스코프 결정 (개선안 A): pathname 기반 1차 결정 + 유저/프로필 정보 기반 2차 보정
  const scope = useMemo(() => {
    return getScopeByPathname(pathname, {
      isLoggedIn: !!effectiveUser,
      hasActiveFreelancer: isFreelancer && isProfileModeInitialized,
      hasActiveCompany: isCompany && isProfileModeInitialized,
      hasFreelancer: effectiveHasFreelancer,
      hasCompany: effectiveHasCompany,
    })
  }, [pathname, effectiveUser, effectiveHasFreelancer, effectiveHasCompany, isProfileModeInitialized, isFreelancer, isCompany])
  
  const pathContext = useMemo(() => {
    return getPathContext(pathname, {
      isLoggedIn: !!effectiveUser,
      hasActiveFreelancer: isFreelancer && isProfileModeInitialized,
      hasActiveCompany: isCompany && isProfileModeInitialized,
      hasFreelancer: effectiveHasFreelancer,
      hasCompany: effectiveHasCompany,
    })
  }, [pathname, effectiveUser, effectiveHasFreelancer, effectiveHasCompany, isProfileModeInitialized, isFreelancer, isCompany])
  const isAuthPage = pathname.startsWith('/auth')
  const isOnboardingPage = pathname.startsWith('/onboarding')
  // ✅ 모바일 + /my/* 에서는 프로필 아이콘(UserMenu) 숨김
  const isMyRoute = pathname.startsWith('/my')
  
  // ✅ 메뉴 구조를 안정화: effectiveUser와 scope가 변경되지 않는 한 메뉴 구조는 유지
  // pathname은 active 상태 계산에만 사용
  const menuStructure = useMemo(() => {
    // effectiveUser가 null이면 비로그인 메뉴
    if (!effectiveUser) {
      // 비로그인: 퍼블릭 영역 메뉴 (프로필 모드 정보 불필요)
      // ✅ 경로 기반 우선 결정: /freelancer/** 경로는 무조건 프리랜서 메뉴 표시
      if (pathname.startsWith('/freelancer')) {
        return { type: 'public-freelancer', isLoggedIn: false } as const
      }
      // ✅ 경로 기반 우선 결정: /client/** 경로는 무조건 클라이언트 메뉴 표시
      if (pathname.startsWith('/client')) {
        return { type: 'public-client', isLoggedIn: false } as const
      }
      
      if (scope === 'client') {
        return { type: 'public-client', isLoggedIn: false } as const
      }
      if (scope === 'freelancer') {
        return { type: 'public-freelancer', isLoggedIn: false } as const
      }
      if (scope === 'public') {
        // 공개 영역: 기본적으로 프리랜서 랜딩으로 간주
        return { type: 'public-freelancer', isLoggedIn: false } as const
      }
      return null
    }
    
    // 로그인 상태: effectiveUser가 있으면 항상 로그인 메뉴 표시
    if (pathContext.isMyPage) {
      // 마이페이지 영역: 항상 로그인 후 메뉴
      // ✅ /my/freelancer 경로일 때는 scope 기반으로 프리랜서 메뉴 강제 표시
      // ✅ /my/client 경로일 때는 scope 기반으로 기업 메뉴 강제 표시
      if (scope === 'freelancer') {
        return {
          type: 'desktop',
          isCompany: false,
          isFreelancer: true,
        } as const
      }
      if (scope === 'client') {
        return {
          type: 'desktop',
          isCompany: true,
          isFreelancer: false,
        } as const
      }
      // scope가 unknown인 경우 프로필 모드 정보 사용
      return {
        type: 'desktop',
        isCompany: isProfileModeInitialized ? effectiveHasCompany : false,
        isFreelancer: isProfileModeInitialized ? effectiveHasFreelancer : false,
      } as const
    }
    
    // 퍼블릭 영역: 로그인 상태이므로 항상 true로 전달 (깜빡임 방지)
    // ✅ 경로 기반 우선 결정: /freelancer/** 경로는 무조건 프리랜서 메뉴 표시
    if (pathname.startsWith('/freelancer')) {
      return {
        type: 'desktop',
        isCompany: false,
        isFreelancer: isProfileModeInitialized ? effectiveHasFreelancer : true, // 프로필 모드 정보가 없어도 프리랜서 메뉴 표시
      } as const
    }
    // ✅ 경로 기반 우선 결정: /client/** 경로는 무조건 클라이언트 메뉴 표시
    if (pathname.startsWith('/client')) {
      return { type: 'public-client', isLoggedIn: true } as const
    }
    
    if (scope === 'client') {
      return { type: 'public-client', isLoggedIn: true } as const
    }
    if (scope === 'freelancer') {
      return { type: 'public-freelancer', isLoggedIn: true } as const
    }
    if (scope === 'public') {
      // 공개 영역에서 로그인한 경우: 기본적으로 프리랜서 랜딩
      // (루트 경로는 이미 freelancer로 보정되므로 여기 도달하지 않음)
      return { type: 'public-freelancer', isLoggedIn: true } as const
    }
    
    return null
  }, [
    effectiveUser, 
    scope, 
    pathname,
    pathContext.isMyPage, 
    isProfileModeInitialized,
    effectiveHasCompany, 
    effectiveHasFreelancer
  ])

  // ✅ 이전 메뉴를 유지하면서 새로운 메뉴로 전환 (깜빡임 방지)
  const previousMenuItemsRef = useRef<HeaderMenuGroup[]>([])
  const desktopMenuItems = useMemo(() => {
    if (!menuStructure) {
      // 메뉴 구조가 없으면 이전 메뉴 유지
      return previousMenuItemsRef.current.length > 0 ? previousMenuItemsRef.current : []
    }

    let newMenuItems: HeaderMenuGroup[] = []
    switch (menuStructure.type) {
      case 'public-client':
        newMenuItems = getPublicClientMenu(menuStructure.isLoggedIn, pathname)
        break
      case 'public-freelancer':
        newMenuItems = getPublicFreelancerMenu(menuStructure.isLoggedIn, pathname)
        break
      case 'desktop':
        newMenuItems = getDesktopHeaderMenu(null, menuStructure.isCompany, menuStructure.isFreelancer, pathname)
        break
      default:
        newMenuItems = []
    }

    // ✅ 메뉴 구조가 변경되지 않았으면 이전 메뉴 유지 (단순화된 비교)
    // pathname 변경 시 active만 바뀌는 상황에서도 안정적인 렌더링 보장
    if (previousMenuItemsRef.current.length > 0 && newMenuItems.length > 0) {
      const prevItems = previousMenuItemsRef.current[0]?.items ?? []
      const newItems = newMenuItems[0]?.items ?? []
      
      // 메뉴 아이템 개수와 key가 같으면 구조 동일 (active만 업데이트)
      if (prevItems.length === newItems.length && 
          prevItems.every((item, idx) => item.key === newItems[idx]?.key)) {
        previousMenuItemsRef.current = newMenuItems
        return newMenuItems
      }
    }

    // 메뉴 구조가 변경되었거나 처음 로드하는 경우
    previousMenuItemsRef.current = newMenuItems
    return newMenuItems
  }, [menuStructure, pathname])

  const {
    unreadCount,
    recentMessages,
    loadingMessages,
    loadNotifications,
    setRecentMessages,
    setUnreadCount,
  } = useNotifications(supabase, effectiveUser, authLoading, modeLoading, scope)

  const authHref = useMemo(() => {
    if (scope === 'client') return '/auth?redirect=%2Fclient&role=client'
    return '/auth'
  }, [scope])

  useEffect(() => {
    setShowMobileMenu(false)
    setShowUserMenu(false)
    setShowNotificationMenu(false)
    setIsNotificationMenuPinned(false)
    setProcessingMessageId(null)
  }, [pathname])

  const showGoFreelancerBtn = Boolean(effectiveUser && scope === 'client')
  const showGoClientBtn = Boolean(effectiveUser && scope === 'freelancer')

  const onClickLogo = () => {
    // ✅ /my/client, /client/* → /client 랜딩
    if (pathname.startsWith('/my/client') || pathname.startsWith('/client')) {
      router.push(CLIENT_LANDING)
    }
    // ✅ /my/freelancer/*, /freelancer/* → / 랜딩
    else if (pathname.startsWith('/my/freelancer') || pathname.startsWith('/freelancer')) {
      router.push(FREELANCER_LANDING)
    }
    // ✅ 기본값: scope 기반
    else if (scope === 'client') {
      router.push(CLIENT_LANDING)
    } else {
      router.push(FREELANCER_LANDING)
    }
  }


  // ✅ 온보딩 중에는 헤더 숨김
  if (isOnboardingPage) {
    return null
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[800] w-full h-[64px] flex items-center border-b border-gray-200/60 bg-white/95 backdrop-saturate-[1.8] backdrop-blur-xl shadow-sm">
        <div className="absolute inset-0 z-[-1] bg-gradient-to-b from-white/95 to-white/90 backdrop-saturate-[1.8] backdrop-blur-xl" />

        <header className="flex h-full w-full items-center px-4 md:px-6 md:max-w-[90%] md:mx-auto">
          <div className="flex items-center h-full gap-8 flex-1">
            <button
              type="button"
              onClick={onClickLogo}
              className="inline-flex items-center"
              aria-label="랜딩으로 이동"
            >
              <Logo />
            </button>

            {/* ✅ 네비: 로그인/비로그인 별로 다르게 보여주되, 전환 링크는 메뉴에 포함 */}
            {/* ✅ min-height, min-width 설정으로 메뉴 깜빡임 방지 (폭 변동 최소화) */}
            <div className="hidden md:flex items-center gap-6 min-h-[24px] min-w-[420px]">
              {desktopMenuItems.length > 0 && desktopMenuItems[0]?.items
                ? desktopMenuItems[0].items.map((item) => {
                    // 전환 링크는 스타일을 다르게 적용
                    const isSwitchLink = item.key === 'switch-to-client' || item.key === 'switch-to-freelancer'
                    return (
                      <Link
                        key={item.key}
                        href={item.href || '#'}
                        className={[
                          'text-sm font-medium transition-colors whitespace-nowrap',
                          isSwitchLink
                            ? item.isActive
                              ? 'text-blue-600'
                              : 'text-blue-600 hover:text-blue-700'
                            : item.isActive
                              ? 'text-blue-600'
                              : 'text-gray-700 hover:text-gray-900',
                        ].join(' ')}
                      >
                        {item.label}
                      </Link>
                    )
                  })
                : null}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-4">
            {/* ✅ effectiveUser 사용: 초기 로드 후에는 메모이제이션된 값 사용 */}
            {effectiveUser ? (
              <>
                {/* ✅ 로그인: 스코프 전환 CTA (우측 고정 버튼) */}
                <div className="hidden md:flex items-center gap-2">
                  {showGoFreelancerBtn && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.push('/freelancer/dashboard')}
                      className="rounded-xl whitespace-nowrap"
                    >
                      프리랜서로 전환
                    </Button>
                  )}
                  {showGoClientBtn && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.push('/client/dashboard')}
                      className="rounded-xl whitespace-nowrap"
                    >
                      고객으로 전환
                    </Button>
                  )}
                </div>

                {/* ✅ 알림 메뉴: public 스코프에서는 미노출 (로그인 유도는 UserMenu에서 처리) */}
                {/* ✅ PC에서는 항상 알림 아이콘 표시, 모바일에서는 /my/* 경로일 때만 숨김 */}
                {/* scope에 따라 다른 알림 메뉴 컴포넌트 사용 */}
                {scope !== 'public' && (
                  <div className={isMyRoute ? 'hidden md:block' : undefined}>
                    {scope === 'client' ? (
                      <ClientNotificationMenu
                        showNotificationMenu={showNotificationMenu}
                        setShowNotificationMenu={setShowNotificationMenu}
                        isNotificationMenuPinned={isNotificationMenuPinned}
                        setIsNotificationMenuPinned={setIsNotificationMenuPinned}
                        unreadCount={unreadCount}
                        recentMessages={recentMessages}
                        loadingMessages={loadingMessages}
                        processingMessageId={processingMessageId}
                        setProcessingMessageId={setProcessingMessageId}
                        setRecentMessages={setRecentMessages}
                        setUnreadCount={setUnreadCount}
                        loadNotifications={loadNotifications}
                        supabase={supabase}
                      />
                    ) : scope === 'freelancer' ? (
                      <FreelancerNotificationMenu
                        showNotificationMenu={showNotificationMenu}
                        setShowNotificationMenu={setShowNotificationMenu}
                        isNotificationMenuPinned={isNotificationMenuPinned}
                        setIsNotificationMenuPinned={setIsNotificationMenuPinned}
                        unreadCount={unreadCount}
                        recentMessages={recentMessages}
                        loadingMessages={loadingMessages}
                        processingMessageId={processingMessageId}
                        setProcessingMessageId={setProcessingMessageId}
                        setRecentMessages={setRecentMessages}
                        setUnreadCount={setUnreadCount}
                        loadNotifications={loadNotifications}
                        supabase={supabase}
                      />
                    ) : (
                      <NotificationMenu
                        showNotificationMenu={showNotificationMenu}
                        setShowNotificationMenu={setShowNotificationMenu}
                        isNotificationMenuPinned={isNotificationMenuPinned}
                        setIsNotificationMenuPinned={setIsNotificationMenuPinned}
                        unreadCount={unreadCount}
                        recentMessages={recentMessages}
                        loadingMessages={loadingMessages}
                        processingMessageId={processingMessageId}
                        setProcessingMessageId={setProcessingMessageId}
                        setRecentMessages={setRecentMessages}
                        setUnreadCount={setUnreadCount}
                        loadNotifications={loadNotifications}
                        supabase={supabase}
                        scope={scope}
                      />
                    )}
                  </div>
                )}

                {/* ✅ PC에서는 항상 프로필 아이콘 표시, 모바일에서는 /my/* 경로일 때만 숨김 */}
                <div className={isMyRoute ? 'hidden md:block' : undefined}>
                  <UserMenu
                    showUserMenu={showUserMenu}
                    setShowUserMenu={setShowUserMenu}
                    supabase={supabase}
                  />
                </div>

                <button
                  onClick={() => setShowMobileMenu((v) => !v)}
                  className="md:hidden p-2"
                  aria-label={isMyRoute ? '관리 메뉴' : '메뉴'}
                  aria-expanded={showMobileMenu}
                  aria-controls="mobile-header-menu-drawer"
                >
                  {showMobileMenu ? (
                    <X size={24} color="#4a4a4a" strokeWidth={1.5} />
                  ) : (
                    <Menu size={24} color="#4a4a4a" strokeWidth={1.5} />
                  )}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => router.push(authHref)}
                  className="hidden sm:inline-flex min-h-[44px] whitespace-nowrap rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                >
                  {isAuthPage ? '회원가입/로그인' : '로그인/회원가입'}
                </Button>

                <button
                  onClick={() => router.push(authHref)}
                  className="sm:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="로그인/회원가입"
                >
                  <UserCircleIcon size={24} color="#4a4a4a" strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>
        </header>
      </div>

      <MobileMenu
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        user={effectiveUser}
        unreadCount={unreadCount}
        router={router}
      />
    </>
  )
}
